<script lang="ts">
	import { superForm, defaults } from "sveltekit-superforms";
	import { zod4 } from "sveltekit-superforms/adapters";
	import { tick } from "svelte";
	import { submitAssociateFormClientSchema } from "@fergmar/contracts/public/forms/associate-form.schema";
	import { trpcBrowser } from "#trpc/client.svelte.js";
	import { formatPhoneNumber } from "#lib/web-ui/helpers.js";
	import { cn } from "#lib/web-ui/ui/utils.js";
	import { Input } from "#lib/web-ui/ui/input/index.js";
	import { Label } from "#lib/web-ui/ui/label/index.js";
	import { Textarea } from "#lib/web-ui/ui/textarea/index.js";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import {
		PlacesAutoCompleteInput,
		VICTORIA_SERVICE_BOUNDS
	} from "#lib/web-ui/ui/places-input/index.js";
	import { PUBLIC_GOOGLE_PLACES_TOKEN } from "$app/env/public";
	import { toast } from "svelte-sonner";
	import { page } from "$app/state";
	import { TurnstileField } from "#lib/web-ui/forms/turnstile/index.js";
	import { getTemplateContext } from "../template-context.svelte";
	import { associateForm } from "../associate-page.content";
	import {
		associateResults,
		recommend,
		type AssociateQuestionName,
		type FollowUpAnswer,
		type SituationAnswer
	} from "@fergmar/contracts/public/associate-leads";
	import { phoneHref } from "@fergmar/ui/home-buyers/home-buyers-team";
	import type { TeamMember } from "../template-types";

	let { associate }: { associate: TeamMember } = $props();

	const templateContext = getTemplateContext();
	const TOTAL_STEPS = 5;
	const questions = [
		{ name: "situation", other: "situationOther", content: associateForm.situation },
		{ name: "timeline", other: "timelineOther", content: associateForm.timeline },
		{ name: "workLevel", other: "workLevelOther", content: associateForm.workLevel }
	] as const;
	const ADDRESS_STEP = questions.length + 1;

	let step = $state(1);
	let submitted = $state(false);
	let headingEl = $state<HTMLElement | null>(null);
	let answered = $state<Record<AssociateQuestionName, boolean>>({
		situation: false,
		timeline: false,
		workLevel: false
	});

	const firstName = $derived(associate.displayName.split(" ")[0]);
	const question = $derived(step <= questions.length ? questions[step - 1] : null);
	const filledSegments = $derived(submitted ? TOTAL_STEPS : step);

	function utmValues() {
		return {
			utmSource: page.url.searchParams.get("utm_source") ?? "",
			utmMedium: page.url.searchParams.get("utm_medium") ?? "",
			utmCampaign: page.url.searchParams.get("utm_campaign") ?? "",
			utmContent: page.url.searchParams.get("utm_content") ?? "",
			utmTerm: page.url.searchParams.get("utm_term") ?? ""
		};
	}

	function pushToDataLayer(eventName: string, extra: Record<string, unknown> = {}) {
		if (typeof window === "undefined" || !window.dataLayer) return;
		window.dataLayer.push({
			event: eventName,
			formId: "associate_form",
			pagePath: window.location.pathname,
			associateSlug: associate.slug,
			...utmValues(),
			...extra
		});
	}

	const form = superForm(defaults(zod4(submitAssociateFormClientSchema)), {
		id: "submit-associate-form",
		SPA: true,
		resetForm: false,
		applyAction: false,
		validators: zod4(submitAssociateFormClientSchema),
		async onUpdate({ form }) {
			if (!form.valid) return;

			try {
				await trpcBrowser.public.forms.submitAssociateForm.mutate({
					...form.data,
					associateSlug: associate.slug,
					pageUrl: page.url.href,
					routeId: page.route.id ?? "",
					...utmValues()
				});
			} catch (e) {
				form.data.turnstileToken = "";
				toast.error(e instanceof Error && e.message ? e.message : associateForm.errorToast);
				pushToDataLayer("form_submission_error");
				return;
			}

			pushToDataLayer("form_step_complete", { formStep: TOTAL_STEPS });
			pushToDataLayer("form_submission_success");
			if (typeof window !== "undefined") window.fbq?.("track", "Lead");
			submitted = true;
		}
	});

	const { form: formData, enhance, errors, submitting, validate } = form;

	const resultKey = $derived(
		recommend($formData.situation, $formData.timeline, $formData.workLevel)
	);
	const result = $derived(associateResults[resultKey]);
	const callHref = $derived(
		associate.workNumber
			? phoneHref(associate.workNumber)
			: `tel:${templateContext.contact.phone}`
	);

	const textHref = $derived(
		associate.workNumber
			? `sms:${associate.workNumber.replace(/\D/g, "")}?&body=${encodeURIComponent(associateForm.result.smsBody(firstName, $formData.address))}`
			: null
	);

	async function focusHeading() {
		await tick();
		headingEl?.focus();
	}

	function completeStep() {
		pushToDataLayer("form_step_complete", { formStep: step });
		step += 1;
		return focusHeading();
	}

	function goBack() {
		if (step === TOTAL_STEPS) $formData.turnstileToken = "";
		step -= 1;
		return focusHeading();
	}

	function choose(name: AssociateQuestionName, key: SituationAnswer | FollowUpAnswer) {
		formData.update((current) => ({ ...current, [name]: key }));
		answered[name] = true;
		if (key !== "other") completeStep();
	}

	async function continueFromAddress() {
		const addressErrors = await validate("address");
		if (!addressErrors) completeStep();
	}
</script>

<div
	class="bg-card text-card-foreground border-border w-full rounded-lg border p-6 shadow-sm md:p-9"
	data-testid="associate-form"
>
	<div class="mb-6 flex gap-1.5" aria-hidden="true">
		{#each { length: TOTAL_STEPS }, index (index)}
			<span
				data-testid="associate-form-segment"
				data-filled={index < filledSegments}
				class={cn(
					"h-1 flex-1 rounded-full",
					index < filledSegments ? "bg-primary" : "bg-muted"
				)}
			></span>
		{/each}
	</div>

	{#if submitted}
		<div data-testid="associate-form-result">
			<p class="text-muted-foreground text-sm font-semibold">
				{associateForm.result.eyebrow}
			</p>
			<h3
				{@attach (element) => element.focus()}
				tabindex="-1"
				class="text-card-foreground mt-2 text-2xl font-semibold tracking-tight outline-none md:text-3xl"
			>
				{result.heading}
			</h3>

			<p class="text-muted-foreground mt-4 text-base leading-relaxed">{result.body}</p>

			<p
				class="bg-muted border-primary text-card-foreground mt-6 rounded border-l-4 p-4 text-sm font-medium"
			>
				{result.nextStep}
			</p>
			<p class="text-card-foreground mt-6 text-base font-semibold">
				{associateForm.result.hasYourAnswers(firstName)}
			</p>
			<div class="mt-4 flex flex-col gap-3 sm:flex-row">
				<Button href={callHref} size="lg" class="h-12 px-6 text-base font-semibold">
					{associateForm.result.callLabel(firstName)}
				</Button>
				{#if textHref}
					<Button
						href={textHref}
						variant="outline"
						size="lg"
						class="text-card-foreground h-12 px-6 text-base font-semibold"
					>
						{associateForm.result.textLabel(firstName)}
					</Button>
				{/if}
			</div>
		</div>
	{:else}
		<form use:enhance method="POST" class="w-full" novalidate>
			<p class="text-muted-foreground text-sm font-semibold">
				{associateForm.stepLabel(step, TOTAL_STEPS)}
			</p>

			{#if question}
				{@const selected = answered[question.name] ? $formData[question.name] : null}
				<fieldset class="mt-2">
					<legend
						bind:this={headingEl}
						tabindex="-1"
						class="text-card-foreground text-2xl font-semibold tracking-tight outline-none md:text-3xl"
					>
						{question.content.question}
					</legend>
					<div class="mt-6 flex flex-col gap-3">
						{#each question.content.answers as answer (answer.key)}
							<button
								type="button"
								aria-pressed={selected === answer.key}
								onclick={() => choose(question.name, answer.key)}
								class={cn(
									"text-card-foreground focus-visible:ring-ring/50 flex w-full cursor-pointer items-center gap-3 rounded border p-4 text-left text-base transition-colors outline-none focus-visible:ring-[3px]",
									selected === answer.key
										? "border-primary bg-background"
										: "border-border bg-muted hover:border-primary"
								)}
							>
								<span
									aria-hidden="true"
									class={cn(
										"h-4 w-4 flex-shrink-0 rounded-full border",
										selected === answer.key
											? "border-primary bg-primary"
											: "border-border bg-background"
									)}
								></span>
								{answer.label}
							</button>
						{/each}
					</div>
				</fieldset>

				{#if selected === "other"}
					<div class="mt-4 space-y-2">
						<Label for={question.other} class="text-card-foreground">
							{question.content.otherPrompt}
						</Label>
						<Textarea
							id={question.other}
							name={question.other}
							bind:value={$formData[question.other]}
							rows={3}
						/>
						{#if $errors[question.other]}
							<p class="text-destructive text-sm">{$errors[question.other]}</p>
						{/if}
						<p class="text-muted-foreground text-sm">
							{associateForm.privacyNote(firstName, templateContext.siteName)}
						</p>
						<Button type="button" onclick={completeStep} class="font-semibold">
							{associateForm.continueLabel}
						</Button>
					</div>
				{/if}
			{:else if step === ADDRESS_STEP}
				<h3
					bind:this={headingEl}
					tabindex="-1"
					class="text-card-foreground mt-2 text-2xl font-semibold tracking-tight outline-none md:text-3xl"
				>
					{associateForm.address.question}
				</h3>
				<p class="text-muted-foreground mt-3 text-base">{associateForm.address.help}</p>
				<div class="mt-6 space-y-2">
					<Label for="associate-address" class="text-card-foreground">
						{associateForm.address.label}
					</Label>
					<PlacesAutoCompleteInput
						id="associate-address"
						placesToken={PUBLIC_GOOGLE_PLACES_TOKEN}
						bounds={VICTORIA_SERVICE_BOUNDS}
						name="address"
						placeholder={associateForm.address.placeholder}
						autocomplete="off"
						bind:value={$formData.address}
						oninput={() => {
							$formData.addressStreetNumber = "";
							$formData.addressRoute = "";
						}}
						onPlaceSelect={(place) => {
							$formData.addressStreetNumber = place.streetNumber;
							$formData.addressRoute = place.route;
						}}
					/>
					{#if $errors.address}
						<p class="text-destructive text-sm">{$errors.address}</p>
					{/if}
				</div>
				<Button type="button" onclick={continueFromAddress} class="mt-4 font-semibold">
					{associateForm.continueLabel}
				</Button>
			{:else}
				<h3
					bind:this={headingEl}
					tabindex="-1"
					class="text-card-foreground mt-2 text-2xl font-semibold tracking-tight outline-none md:text-3xl"
				>
					{associateForm.contact.question}
				</h3>
				<div class="mt-6 space-y-4">
					<div class="space-y-2">
						<Label for="associate-name" class="text-card-foreground">
							{associateForm.contact.nameLabel}
						</Label>
						<Input
							id="associate-name"
							name="name"
							type="text"
							bind:value={$formData.name}
							autocomplete="name"
							aria-invalid={$errors.name ? "true" : undefined}
						/>
						{#if $errors.name}
							<p class="text-destructive text-sm">{$errors.name}</p>
						{/if}
					</div>
					<div class="space-y-2">
						<Label for="associate-phone" class="text-card-foreground">
							{associateForm.contact.phoneLabel}
						</Label>
						<Input
							id="associate-phone"
							name="phone"
							type="tel"
							bind:value={$formData.phone}
							autocomplete="tel"
							aria-invalid={$errors.phone ? "true" : undefined}
							onchange={(e) => {
								$formData.phone = formatPhoneNumber(e.currentTarget.value);
							}}
						/>
						{#if $errors.phone}
							<p class="text-destructive text-sm">{$errors.phone}</p>
						{/if}
					</div>
					<div class="space-y-2">
						<Label for="associate-email" class="text-card-foreground">
							{associateForm.contact.emailLabel}
						</Label>
						<Input
							id="associate-email"
							name="email"
							type="email"
							bind:value={$formData.email}
							autocomplete="email"
							aria-invalid={$errors.email ? "true" : undefined}
							onchange={(e) => {
								$formData.email = e.currentTarget.value.toLowerCase();
							}}
						/>
						{#if $errors.email}
							<p class="text-destructive text-sm">{$errors.email}</p>
						{/if}
					</div>
					<p class="text-muted-foreground text-sm">
						{associateForm.privacyNote(firstName, templateContext.siteName)}
					</p>
					<div>
						<TurnstileField bind:token={$formData.turnstileToken} />
					</div>
					<Button
						type="submit"
						disabled={$submitting || !$formData.turnstileToken}
						aria-busy={$submitting}
						size="lg"
						class="h-12 w-full text-base font-semibold"
					>
						{$submitting ? associateForm.submittingLabel : associateForm.submitLabel}
					</Button>
				</div>
			{/if}

			{#if step > 1}
				<button
					type="button"
					onclick={goBack}
					class="text-muted-foreground hover:text-card-foreground mt-6 cursor-pointer text-sm underline underline-offset-4"
				>
					{associateForm.backLabel}
				</button>
			{/if}
		</form>
	{/if}
</div>
